import React from 'react';

const Privacy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-8">Privacy Policy</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Last updated: December 2024</p>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">1. Information We Collect</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              At Lunexa, we collect information to provide better services to our users. We collect information in the following ways:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Information You Provide to Us:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Account credentials and profile information</li>
              <li>Payment and billing information</li>
              <li>Project requirements and business information</li>
              <li>Communications with our team (emails, chat messages, support tickets)</li>
              <li>Feedback, reviews, and survey responses</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Information We Collect Automatically:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage data (pages visited, time spent, click patterns)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Log files and analytics data</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">2. How We Use Your Information</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">We use the information we collect for the following purposes:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Service Delivery:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Provide, maintain, and improve our IT automation, development, and AI services</li>
              <li>Process transactions and manage billing</li>
              <li>Deliver project milestones and communicate progress</li>
              <li>Provide technical support and customer service</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Communication:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Send service-related notifications and updates</li>
              <li>Respond to inquiries and support requests</li>
              <li>Share relevant industry insights and company news</li>
              <li>Conduct satisfaction surveys and gather feedback</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Business Operations:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Analyze usage patterns to improve our services</li>
              <li>Prevent fraud and ensure security</li>
              <li>Comply with legal obligations</li>
              <li>Develop new features and services</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">3. Information Sharing and Disclosure</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Service Providers:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Trusted third-party vendors who assist in service delivery</li>
              <li>Payment processors for billing and transaction processing</li>
              <li>Cloud hosting providers for data storage and processing</li>
              <li>Analytics providers to help us understand service usage</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Legal Requirements:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>To comply with applicable laws, regulations, or legal processes</li>
              <li>To respond to lawful requests from public authorities</li>
              <li>To protect our rights, property, or safety, or that of our users</li>
              <li>To investigate and prevent fraud or security issues</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Business Transfers:</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction, 
              subject to the same privacy protections.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">4. Data Security and Protection</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We implement comprehensive security measures to protect your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Employee training on data protection practices</li>
              <li>Secure backup and disaster recovery procedures</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              While we strive to protect your information using industry-standard practices, no method of transmission over the internet 
              or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your data.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">5. Your Privacy Rights</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">You have the following rights regarding your personal information:</p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Access and Portability:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Request access to your personal information</li>
              <li>Receive a copy of your data in a portable format</li>
              <li>Understand how your information is being used</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Correction and Deletion:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Correct inaccurate or incomplete information</li>
              <li>Request deletion of your personal information</li>
              <li>Withdraw consent for data processing</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Control and Objection:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Object to certain types of data processing</li>
              <li>Opt-out of marketing communications</li>
              <li>Restrict processing of your information</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">6. Cookies and Tracking Technologies</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We use cookies and similar technologies to improve your experience and analyze website usage:
            </p>
            
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">Types of Cookies:</h3>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how visitors use our site</li>
              <li><strong>Functional Cookies:</strong> Remember your preferences and settings</li>
              <li><strong>Marketing Cookies:</strong> Used to deliver relevant advertisements</li>
            </ul>
            
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              You can control cookie settings through your browser preferences. Note that disabling certain cookies may affect 
              website functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">7. Data Retention</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>Account information: Retained while your account is active</li>
              <li>Project data: Retained for the duration of the project plus 3 years for support purposes</li>
              <li>Communication records: Retained for 7 years for business and legal purposes</li>
              <li>Analytics data: Aggregated and anonymized data may be retained indefinitely</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">8. International Data Transfers</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              As a technology company serving global clients, we may transfer your information to countries other than your own. 
              When we do so, we ensure appropriate safeguards are in place to protect your information in accordance with this 
              privacy policy and applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">9. Children's Privacy</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information 
              from children under 18. If we become aware that we have collected personal information from a child under 18, we will 
              take steps to delete such information promptly.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">10. Changes to This Privacy Policy</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We may update this privacy policy from time to time to reflect changes in our practices or applicable laws. When we make changes:
            </p>
            <ul className="list-disc pl-6 text-gray-600 dark:text-gray-400 mb-4">
              <li>We will update the "Last updated" date at the top of this policy</li>
              <li>We will notify you via email or through our services for material changes</li>
              <li>We will provide reasonable notice before changes take effect</li>
            </ul>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We encourage you to review this policy periodically to stay informed about how we protect your information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">11. Contact Us</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              If you have any questions about this privacy policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700">
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Email:</strong> support@lunexa.dpdns.org<br />
                <strong>Website:</strong> www.lunexa.com<br />
                <strong>Business Hours:</strong> Monday - Friday, 9:00 AM - 6:00 PM
              </p>
              <p className="text-gray-600 dark:text-gray-400 mt-4">
                <strong>Data Protection Officer:</strong> privacy@lunexa.dpdns.org
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Privacy;